<template>
  <footer class="bg-charcoal-blue text-white py-16 border-t-4 border-oxidized-copper" role="contentinfo">
    <div class="max-w-[1400px] mx-auto px-4 py-12 sm:px-6 md:px-8 md:py-16 grid grid-cols-1 md:grid-cols-2 items-center gap-12">
      <div class="flex items-center gap-4">
        <button
          v-if="logoUrl && canOpenOwnerLogoModal"
          type="button"
          class="w-10 h-10 min-h-[44px] min-w-[44px] shrink-0 border-2 rms-t2-accent-border rms-t2-accent flex items-center justify-center overflow-hidden bg-white cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal-blue"
          :aria-label="$t('public.ownerEditLogoAria')"
          @click="openOwnerLogoModal"
        >
          <img
            :src="logoUrl"
            :alt="restaurantName || 'Restaurant'"
            class="w-full h-full object-contain"
          >
        </button>
        <div v-else-if="logoUrl" class="w-10 h-10 shrink-0 border-2 rms-t2-accent-border rms-t2-accent flex items-center justify-center overflow-hidden bg-white">
          <img
            :src="logoUrl"
            :alt="restaurantName || 'Restaurant'"
            class="w-full h-full object-contain"
          >
        </div>
        <button
          v-else-if="isOwner && ownerViewMode && canOpenOwnerLogoModal"
          type="button"
          class="shrink-0 max-w-[200px] rounded border-0 bg-transparent p-0 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal-blue"
          :aria-label="$t('public.ownerEditLogoAria')"
          @click="openOwnerLogoModal"
        >
          <div class="py-2 px-3 rounded border border-white/30 bg-white/10">
            <p class="text-xs font-mono text-white/80">{{ $t('public.noLogoSetOwnerMessage') }}</p>
          </div>
        </button>
        <div v-else-if="isOwner && ownerViewMode" class="shrink-0 max-w-[200px] py-2 px-3 rounded border border-white/30 bg-white/10">
          <p class="text-xs font-mono text-white/80">{{ $t('public.noLogoSetOwnerMessage') }}</p>
        </div>
        <span class="heading-utilitarian text-3xl font-extrabold tracking-tighter">{{ restaurantName || 'Restaurant' }}</span>
      </div>
      <div class="md:text-right space-y-2 flex flex-col items-center md:items-end">
        <div class="flex flex-wrap items-center justify-center gap-4 min-h-[44px]">
          <button
            type="button"
            class="text-white/80 hover:text-white font-mono text-xs uppercase tracking-widest min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper rounded"
            @click="openLegalModal('terms')"
          >
            {{ $t('app.termsOfService') }}
          </button>
          <button
            type="button"
            class="text-white/80 hover:text-white font-mono text-xs uppercase tracking-widest min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper rounded"
            @click="openLegalModal('privacy')"
          >
            {{ $t('app.privacyPolicy') }}
          </button>
          <button
            type="button"
            class="text-white/80 hover:text-white font-mono text-xs uppercase tracking-widest min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxidized-copper rounded"
            @click="openCookiePrefs"
          >
            {{ $t('landing.footer.cookies') }}
          </button>
        </div>
        <p class="font-mono text-xs uppercase tracking-widest opacity-60">© {{ currentYear }} {{ restaurantName || 'Restaurant' }}. System Status: Nominal.</p>
        <PublicFooterCredits v-if="showCredits" class=" md:justify-end mt-2 text-white/70 [&_a]:text-white/70 [&_span]:text-white/70" compact />
      </div>
    </div>
    <LegalContentModal
      :open="legalModalOpen"
      :type="legalModalType"
      :locale="modalLocale"
      @close="closeLegalModal"
    />
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import LegalContentModal from '@/components/legal/LegalContentModal.vue'
import PublicFooterCredits from '@/components/public/PublicFooterCredits.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'
import { useCookieConsentStore } from '@/stores/sitePrefs'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()
const cookieConsentStore = useCookieConsentStore()

const props = defineProps({
  restaurantName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
  showCredits: { type: Boolean, default: true },
  modalLocale: { type: String, default: 'en' },
})

const isOwner = computed(() => props.viewer?.is_owner === true)

const legalModalOpen = ref(false)
const legalModalType = ref('terms')

const currentYear = computed(() => new Date().getFullYear())

function openLegalModal(type) {
  legalModalType.value = type
  legalModalOpen.value = true
}

function closeLegalModal() {
  legalModalOpen.value = false
}

function openCookiePrefs() {
  cookieConsentStore.openPrefs()
}
</script>
